import React, { useState } from 'react';
import FileUpload from './FileUpload';
import './EditItemModal.css';

const EditItemModal = ({ item, itemIndex, onSave, onClose, galleryType }) => {
  const [editedItem, setEditedItem] = useState({ ...item });
  const [showFileUpload, setShowFileUpload] = useState(false);

  const handleFieldChange = (field, value) => {
    setEditedItem(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMediaReplace = (fileData) => {
    // Create updated item with new media
    const updatedItem = {
      ...editedItem,
      type: fileData.type,
      data: fileData.data,
      thumbnail: fileData.thumbnail,
      size: fileData.size,
      width: fileData.width,
      height: fileData.height,
      duration: fileData.type === 'video' ? formatDuration(fileData.duration || 0) : editedItem.duration,
      icon: fileData.type === 'video' ? 'fas fa-play-circle' : 'fas fa-camera',
      lastModified: fileData.lastModified
    };
    
    setEditedItem(updatedItem);
    setShowFileUpload(false);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isLegacyItem = () => {
    return !editedItem.data && !editedItem.thumbnail && !editedItem.type;
  };

  const handleSave = () => {
    onSave(itemIndex, editedItem);
    onClose();
  };

  const getAcceptedTypes = () => {
    switch (galleryType) {
      case 'photos':
        return 'image/*';
      case 'videos':
        return 'video/*';
      default:
        return 'image/*,video/*';
    }
  };

  return (
    <div className="edit-item-modal-overlay" onClick={onClose}>
      <div className="edit-item-modal" onClick={(e) => e.stopPropagation()}>
        <div className="edit-item-header">
          <h3>
            <i className="fas fa-sliders-h"></i>
            Modifier l'élément
          </h3>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="edit-item-content">
          {/* Current Media Preview */}
          <div className="current-media-section">
            <h4>Aperçu actuel</h4>
            <div className="current-media-preview">
              {editedItem.data && editedItem.type === 'image' ? (
                <img 
                  src={editedItem.data} 
                  alt={editedItem.title}
                  className="media-preview-large"
                />
              ) : editedItem.thumbnail && editedItem.type === 'video' ? (
                <div className="video-preview-container">
                  <img 
                    src={editedItem.thumbnail} 
                    alt={editedItem.title}
                    className="media-preview-large"
                  />
                  <div className="video-overlay">
                    <i className="fas fa-play-circle"></i>
                    {editedItem.duration && (
                      <span className="video-duration-large">{editedItem.duration}</span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="placeholder-preview">
                  <i className={editedItem.icon || 'fas fa-file'} style={{ fontSize: '3rem' }}></i>
                  <p>Aucun média téléchargé</p>
                  {isLegacyItem() && (
                    <p className="legacy-note">
                      <i className="fas fa-info-circle"></i>
                      Élément existant - ajoutez un média pour le remplacer
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Item Details */}
          <div className="item-details-section">
            <h4>Détails de l'élément</h4>
            
            <div className="form-group">
              <label>Titre</label>
              <input
                type="text"
                value={editedItem.title || ''}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                placeholder="Titre de l'élément"
                className="title-input"
              />
            </div>

            {editedItem.type === 'video' && !isLegacyItem() && (
              <div className="form-group">
                <label>Durée</label>
                <input
                  type="text"
                  value={editedItem.duration || ''}
                  onChange={(e) => handleFieldChange('duration', e.target.value)}
                  placeholder="0:00"
                  className="duration-input"
                />
              </div>
            )}

            {!isLegacyItem() && (
              <div className="media-info">
                <h5>Informations du fichier</h5>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Type:</span>
                    <span>{editedItem.type || 'Non défini'}</span>
                  </div>
                  {editedItem.size && (
                    <div className="info-item">
                      <span className="info-label">Taille:</span>
                      <span>{Math.round(editedItem.size / 1024)} KB</span>
                    </div>
                  )}
                  {editedItem.width && editedItem.height && (
                    <div className="info-item">
                      <span className="info-label">Dimensions:</span>
                      <span>{editedItem.width} × {editedItem.height}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Media Replacement */}
          <div className="media-replacement-section">
            <h4>
              Remplacer le média
              <button
                type="button"
                className="toggle-upload-btn"
                onClick={() => setShowFileUpload(!showFileUpload)}
              >
                <i className={`fas ${showFileUpload ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                {showFileUpload ? 'Masquer' : 'Afficher'} l'upload
              </button>
            </h4>
            
            {showFileUpload && (
              <div className="upload-section">
                <FileUpload
                  onFileUpload={handleMediaReplace}
                  acceptedTypes={getAcceptedTypes()}
                  maxSize={52428800}
                  label="Remplacer par un nouveau fichier"
                />
                <p className="upload-note">
                  <i className="fas fa-info-circle"></i>
                  Le nouveau fichier remplacera complètement le contenu actuel
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="edit-item-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            <i className="fas fa-times"></i>
            Annuler
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            <i className="fas fa-save"></i>
            Sauvegarder les modifications
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditItemModal;
